import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand } from '@fortawesome/free-solid-svg-icons';

const statusIcons = {
  reporting: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  Default: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
};

const getIcon = (status) => {
  return new L.Icon({
    iconUrl: statusIcons[status] || statusIcons.Default,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

const Map = ({ nodes, mapHeight }) => {
  const mapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (mapRef.current !== null) {
      mapRef.current.off();
      mapRef.current.remove();
    }

    mapRef.current = L.map('map').setView([41.85, -87.65], 10);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> Contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
    }).addTo(mapRef.current);

    nodes.forEach((node) => {
      const { lat, lng } = node.coords;
      if (!lat || !lng) return;

      const marker = L.marker([lat, lng], { icon: getIcon(node.status) }).addTo(mapRef.current);

      const popupContent = `
        <div style="font-family: Arial, sans-serif; font-size: 14px;">
          <strong>Node:</strong> <a href="https://portal.sagecontinuum.org/node/${node.node}" target="_blank">${node.node}</a><br>
          <strong>Status:</strong> Online<br>
          <strong>City:</strong> ${node.city}<br>
          <div style="text-align: center; margin-top: 10px;">
            <button id="viewDataButton-${node.node}" style="padding: 5px 10px; background-color: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer;">
              View Real-time Data
            </button>
          </div>
        </div>
      `;

      marker.bindPopup(popupContent);

      marker.on('popupopen', () => {
        const button = document.getElementById(`viewDataButton-${node.node}`);
        if (button) {
          button.addEventListener('click', () => {
            window.open(`/node/${node.node}`, '_blank');
          });
        }
      });
    });

    const zoomToLocationHandler = (event) => {
      const { lat, lng } = event.detail;
      mapRef.current.setView([lat, lng], 10);
    };

    window.addEventListener('zoomToLocation', zoomToLocationHandler);

    return () => {
      window.removeEventListener('zoomToLocation', zoomToLocationHandler);
    };
  }, [nodes, navigate]);

  const openMapInNewWindow = () => {
    const mapWindow = window.open('', '_blank');
    const nodesData = JSON.stringify(nodes);
    const currentUrl = window.location.origin; // Get the current base URL
    
    mapWindow.document.write(`
      <html>
        <head>
          <title>Environmental Nodes Map</title>
          <link rel="stylesheet" href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css" />
          <style>
            html, body, #fullscreen-map {
              height: 100%;
              margin: 0;
              padding: 0;
            }
            .custom-popup {
              font-family: Arial, sans-serif;
              font-size: 14px;
            }
            .view-data-button {
              padding: 5px 10px;
              background-color: #28a745;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              margin-top: 10px;
              width: 100%;
            }
            .view-data-button:hover {
              background-color: #218838;
            }
          </style>
        </head>
        <body>
          <div id="fullscreen-map"></div>
          <script src="https://unpkg.com/leaflet@1.7.1/dist/leaflet.js"></script>
          <script>
            const nodes = ${nodesData};
            const baseUrl = "${currentUrl}";
            const map = L.map('fullscreen-map').setView([41.85, -87.65], 4);
            
            L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
              maxZoom: 19,
              attribution: '© OpenStreetMap contributors'
            }).addTo(map);

            const getIcon = (status) => {
              return L.icon({
                iconUrl: status === 'reporting' 
                  ? 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png'
                  : 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41]
              });
            };

            nodes.forEach(node => {
              if (node.coords && node.coords.lat && node.coords.lng) {
                const marker = L.marker([node.coords.lat, node.coords.lng], {
                  icon: getIcon(node.status)
                }).addTo(map);

                const popupContent = \`
                  <div class="custom-popup">
                    <strong>Node:</strong> <a href="https://portal.sagecontinuum.org/node/\${node.node}" target="_blank">\${node.node}</a><br>
                    <strong>Status:</strong> Online<br>
                    <strong>City:</strong> \${node.city}<br>
                    <button class="view-data-button" onclick="window.open('\${baseUrl}/node/\${node.node}', '_blank')">
                      View Real-time Data
                    </button>
                  </div>
                \`;

                marker.bindPopup(popupContent);
              }
            });

            // Fit the map to show all markers
            const bounds = L.featureGroup(nodes.map(node => 
              L.marker([node.coords.lat, node.coords.lng])
            )).getBounds();
            map.fitBounds(bounds);
          </script>
        </body>
      </html>
    `);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div id="map" style={{ height: `${mapHeight}px`, width: '100%' }}></div>
      <button
        onClick={openMapInNewWindow}
        style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1000 }}
        className="bg-white hover:bg-gray-100 text-black font-bold py-2 px-4 rounded border border-gray-300"
      >
        <FontAwesomeIcon icon={faExpand} />
      </button>
    </div>
  );
};

export default Map;